/* eslint-disable react/no-unescaped-entities */
import { basePath } from '@/lib/base';
import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Copy, Check, Store as StoreIcon, Globe, Zap, Link as LinkIcon, ArrowRight } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { usePlatform } from '@/hooks/usePlatform';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function CopyBlock({ title, hint, code, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success('Copié');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Impossible de copier');
    }
  };
  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-zinc-700 uppercase tracking-wide">{title}</div>
        <Button
          size="sm"
          variant="outline"
          onClick={copy}
          data-testid={`${testId}-copy`}
          className="h-7 px-2.5 rounded-md text-xs"
        >
          {copied ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
          {copied ? 'Copié' : 'Copier'}
        </Button>
      </div>
      {hint && <div className="text-xs text-zinc-500">{hint}</div>}
      <pre className="bg-zinc-900 text-zinc-100 rounded-md text-xs p-3 overflow-x-auto whitespace-pre font-mono leading-relaxed border border-zinc-800">
        {code}
      </pre>
    </div>
  );
}

export default function Snippet() {
  const { matchStore, platform, isWoo } = usePlatform();
  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [storeId, setStoreId] = useState('');

  useEffect(() => {
    (async () => {
      const [s, p] = await Promise.all([api.get('/stores'), api.get('/products')]);
      setStores(s.data);
      setProducts(p.data);
    })();
  }, []);

  const platformStores = stores.filter(matchStore);
  // Keep the selected store valid for the active platform.
  useEffect(() => {
    if (platformStores.length === 0) { setStoreId(''); return; }
    if (!platformStores.some((s) => s.id === storeId)) setStoreId(platformStores[0].id);
  }, [stores, storeId, platform]); // eslint-disable-line

  const store = stores.find((s) => s.id === storeId);
  const storeProducts = useMemo(
    () => products.filter((p) => p.store_id === storeId),
    [products, storeId]
  );

  const universalSnippet = useMemo(() => {
    if (!store) return '';
    const hasCustomDomain = (store.custom_domains || []).length > 0;
    const customHostNote = hasCustomDomain
      ? `\n<!-- Checkout URL ciblée : https://${store.custom_domains[0]} (domaine personnalisé) -->`
      : `\n<!-- Astuce : ajoutez un domaine personnalisé dans Boutiques > Domaines pour que vos clients ne voient pas l'URL principale de la plateforme. -->`;
    return `<!-- External Checkout — Universal snippet v5 (paste once, works for ALL products · Shopify + WooCommerce · no-referrer · variant-aware) -->${customHostNote}
<script src="${BACKEND_URL}/api/snippet.js?v=5&store=${store.id}"
        data-store-id="${store.id}"
        defer></script>`;
  }, [store]);

  const vitrineSnippet = useMemo(() => {
    if (!store) return '';
    return `<!-- External Checkout — Vitrine universal snippet (works for the whole site) -->
<script src="${BACKEND_URL}/api/snippet.js?v=5&store=${store.id}"
        data-store-id="${store.id}"
        defer></script>

<!-- Then on each product element, add the data attribute: -->
<button data-vitrine-product="<vitrine_product_id>"
        data-qty="1"
        class="your-button-class">
  Acheter — 29,99 €
</button>

<!-- OR call the global API from your custom button: -->
<script>
  document.querySelector('#my-buy-btn').onclick = function () {
    window.externalCheckout.go([
      { vitrine_id: '<vitrine_product_id>', qty: 1 },
    ]);
  };
</script>`;
  }, [store]);

  const directLinkForProduct = (p) => {
    // Use the store's first custom_domain if defined, else fall back to main URL.
    const customHost = (store?.custom_domains || [])[0];
    const base = customHost ? `https://${customHost}` : BACKEND_URL;
    return `${base}/checkout/${p.id}`;
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-5xl">
      <div className="mb-8">
        <div className="overline mb-2">— Intégration</div>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Snippets de paiement{isWoo ? ' · WordPress' : platform === 'shopify' ? ' · Shopify' : ''}</h1>
        <p className="text-zinc-600 mt-2">
          Un <strong>seul script</strong> par boutique, à coller <strong>une fois</strong>. Il n'intercepte que le bouton
          <strong> « {isWoo ? 'Commander' : 'Procéder au paiement'} »</strong> — le reste de la navigation reste 100% natif.
        </p>
      </div>

      <Card className="card-flat p-6 mb-6">
        <label className="overline mb-3 block">— Boutique</label>
        <Select value={storeId} onValueChange={setStoreId}>
          <SelectTrigger
            data-testid="admin-snippet-store-select"
            className="h-11 rounded-md max-w-md"
          >
            <SelectValue placeholder="Choisir une boutique" />
          </SelectTrigger>
          <SelectContent>
            {platformStores.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Card>

      {store && (
        <div className="space-y-6">
          {/* Custom domain status banner */}
          {(store.custom_domains || []).length === 0 ? (
            <RouterLink to={`${basePath()}/hosting`} className="block">
              <Card className="card-flat p-5 border-2 border-dashed border-amber-300 bg-amber-50 hover:bg-amber-100 transition cursor-pointer">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <Globe className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-amber-900 text-sm">Aucun domaine personnalisé configuré pour cette boutique</div>
                      <div className="text-xs text-amber-800 mt-1">
                        Vos clients atterriront sur l'URL principale <code className="font-mono">{BACKEND_URL}</code> à la place de votre domaine.
                        Configurez un domaine personnalisé pour un branding professionnel et l'isolation du trafic.
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-xs font-semibold text-amber-700 shrink-0">
                    Configurer <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              </Card>
            </RouterLink>
          ) : (
            <Card className="card-flat p-4 border border-emerald-200 bg-emerald-50">
              <div className="flex items-center gap-3">
                <Globe className="h-4 w-4 text-emerald-600 shrink-0" />
                <div className="text-xs text-emerald-900 flex-1">
                  Domaine de paiement personnalisé actif : <code className="font-mono font-bold">{store.custom_domains[0]}</code>
                </div>
                <RouterLink to={`${basePath()}/hosting`} className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 flex items-center gap-1 shrink-0">
                  Gérer <ArrowRight className="h-3 w-3" />
                </RouterLink>
              </div>
            </Card>
          )}

          {/* Universal install snippet — platform-specific guide */}
          <Card className="card-flat p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className={`w-10 h-10 rounded-md text-white flex items-center justify-center shrink-0 ${isWoo ? 'bg-violet-600' : 'bg-zinc-900'}`}>
                {isWoo ? <Globe className="h-5 w-5" /> : <StoreIcon className="h-5 w-5" />}
              </div>
              <div className="flex-1">
                <h2 className="font-display text-xl font-bold tracking-tight">
                  {isWoo ? 'Boutique WordPress' : 'Boutique Shopify'} · {store.name}
                </h2>
                <p className="text-sm text-zinc-600 mt-1">
                  Un <strong>seul script</strong> à coller <strong>une fois</strong>. Il n'intercepte que le bouton
                  <strong> « {isWoo ? 'Commander / Procéder au paiement' : 'Procéder au paiement'} »</strong> — le reste de votre
                  boutique reste 100% natif.
                </p>
              </div>
            </div>

            {/* Step-by-step install */}
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 mb-5" data-testid="snippet-install-steps">
              <div className="overline mb-3">— Installation en 3 étapes ({isWoo ? 'WordPress' : 'Shopify'})</div>
              <ol className="space-y-2.5">
                {(isWoo ? [
                  <>Dans WordPress, allez dans <b>Extensions → Ajouter</b>, installez et activez le plugin gratuit <b>WPCode</b> (ou <b>Insert Headers and Footers</b>).</>,
                  <>Ouvrez <b>Code Snippets → Header &amp; Footer</b> (WPCode) ou <b>Réglages → Insert Headers and Footers</b>.</>,
                  <>Collez le script ci-dessous dans le champ <b>« Pied de page »</b> (Footer) — celui dont la note indique <i>« Ces scripts seront visibles juste au-dessus de la balise fermante <code className="font-mono bg-white border border-zinc-200 px-1 rounded text-[11px]">&lt;/body&gt;</code> »</i> — puis cliquez sur <b>Enregistrer</b>. ⚠️ Surtout pas dans « En-tête / Header ».</>,
                ] : [
                  <>Dans votre admin Shopify, allez dans <b>Boutique en ligne → Thèmes</b>.</>,
                  <>Sur votre thème, cliquez sur <b>⋯ (Actions) → Modifier le code</b>.</>,
                  <>Ouvrez le fichier <b>theme.liquid</b>, collez le script <b>juste avant</b> <code className="font-mono bg-white border border-zinc-200 px-1 rounded text-xs">&lt;/body&gt;</code>, puis <b>Enregistrez</b>.</>,
                ]).map((txt, i) => (
                  <li key={i} className="flex gap-3 text-sm text-zinc-700">
                    <span className="shrink-0 h-6 w-6 rounded-full bg-zinc-900 text-white text-xs font-bold grid place-items-center">{i + 1}</span>
                    <span className="leading-relaxed">{txt}</span>
                  </li>
                ))}
              </ol>
            </div>

            <CopyBlock
              title="Script à coller"
              hint={isWoo
                ? "À coller dans le Footer du site (via WPCode / Insert Headers and Footers). Fonctionne pour tous les produits."
                : "À coller dans theme.liquid avant </body>. N'intercepte QUE le bouton checkout du panier (drawer compris)."}
              code={universalSnippet}
              testId="snippet-shopify-universal"
            />
            {!isWoo && (
            <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
              <div className="bg-zinc-50 border border-zinc-100 rounded-md p-3">
                <div className="flex items-center gap-1.5 font-semibold text-zinc-700 mb-1"><Zap className="h-3 w-3" /> Ajout panier</div>
                <div className="text-zinc-500">Comportement Shopify natif — pas d'interception</div>
              </div>
              <div className="bg-zinc-50 border border-zinc-100 rounded-md p-3">
                <div className="flex items-center gap-1.5 font-semibold text-zinc-700 mb-1"><Zap className="h-3 w-3" /> Validation panier</div>
                <div className="text-zinc-500">Lit <code className="font-mono">/cart.js</code> → redirige avec tous les items</div>
              </div>
              <div className="bg-zinc-50 border border-zinc-100 rounded-md p-3">
                <div className="flex items-center gap-1.5 font-semibold text-zinc-700 mb-1"><Zap className="h-3 w-3" /> Cart drawer</div>
                <div className="text-zinc-500">Détecte le panier latéral des thèmes modernes</div>
              </div>
            </div>
            )}
          </Card>

          {/* Universal Vitrine snippet */}
          <Card className="card-flat p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-md bg-blue-600 text-white flex items-center justify-center shrink-0">
                <Globe className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h2 className="font-display text-xl font-bold tracking-tight">Site vitrine</h2>
                <p className="text-sm text-zinc-600 mt-1">
                  Collez le script <strong>une seule fois</strong> dans le <code className="font-mono bg-zinc-100 px-1.5 py-0.5 rounded text-xs">&lt;head&gt;</code> de votre site vitrine.
                  Deux modes au choix : attribut <code className="font-mono bg-zinc-100 px-1.5 py-0.5 rounded text-xs">data-vitrine-product</code> OU appel JS via <code className="font-mono bg-zinc-100 px-1.5 py-0.5 rounded text-xs">window.externalCheckout.go()</code>.
                </p>
              </div>
            </div>
            <CopyBlock
              title="Snippet universel + exemples"
              code={vitrineSnippet}
              testId="snippet-vitrine-universal"
            />
          </Card>

          {/* Direct links — fallback for emails, SMS, Instagram bio… */}
          <Card className="card-flat p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-md bg-zinc-200 text-zinc-700 flex items-center justify-center shrink-0">
                <LinkIcon className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h2 className="font-display text-xl font-bold tracking-tight">Liens directs (email / SMS / bio)</h2>
                <p className="text-sm text-zinc-600 mt-1">
                  Pour les paniers à 1 produit hors automatisation : un lien direct pré-rempli.
                </p>
              </div>
            </div>
            <div className="space-y-2">
              {storeProducts.length === 0 && (
                <div className="text-sm text-zinc-500">Aucun produit pour cette boutique.</div>
              )}
              {storeProducts.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-3 py-2 border-b border-zinc-100 last:border-b-0">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{p.name}</div>
                    <code className="text-[11px] text-zinc-500 font-mono truncate block" data-testid={`direct-link-${p.id}`}>{directLinkForProduct(p)}</code>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-md h-8 text-xs shrink-0"
                    data-testid={`direct-link-copy-${p.id}`}
                    onClick={async () => {
                      await navigator.clipboard.writeText(directLinkForProduct(p));
                      toast.success('Lien copié');
                    }}
                  >
                    <Copy className="h-3 w-3 mr-1" /> Copier
                  </Button>
                </div>
              ))}
            </div>
          </Card>

          {/* How it works */}
          <Card className="card-flat p-6 bg-zinc-50">
            <div className="overline mb-3">— Comment ça marche</div>
            <ol className="text-sm text-zinc-700 space-y-2 list-decimal list-inside">
              <li>Le client navigue normalement sur votre {isWoo ? 'site WordPress / WooCommerce' : 'boutique Shopify'} ou votre site vitrine.</li>
              <li>Il ajoute des produits à son panier — <strong>aucune redirection</strong>, expérience habituelle.</li>
              <li>Quand il clique <strong>« {isWoo ? 'Commander' : 'Procéder au paiement'} »</strong>, le snippet intercepte le clic.</li>
              <li>Il construit l'URL <code className="font-mono bg-zinc-100 px-1 rounded text-xs">/checkout?cart=...</code> contenant TOUS les produits du panier.</li>
              <li>Le client atterrit sur votre checkout one-page (white-label, branding de la boutique).</li>
              <li>Notre backend résout les IDs {isWoo ? 'WooCommerce' : 'Shopify'} → produits internes. Les produits non mappés sont ignorés silencieusement.</li>
              <li>Paiement Stripe/Viva avec metadata vitrine, commande {isWoo ? 'WooCommerce' : 'Shopify'} créée auto avec frais de livraison.</li>
            </ol>
          </Card>
        </div>
      )}
    </div>
  );
}
